/**
 * t641u: a message turned into a task shows as the author's normal bubble with one compact task row attached
 * beneath it, like a reply: "<Person> turned this into a task", the key, the title and its live status. The old
 * system note ("turned this into a task: …", posted as a thread reply) is no longer sent and is never shown.
 * Used by the web (computer and phone) and the app.
 */
import type { MessageTaskLink } from './types';
import { mixHex, parseHex } from './branding/color';

export const TASK_FROM_MESSAGE_EVENT = 'task.created_from_message';

/** The old "turned this into a task" system note: hidden everywhere (the task row under the message replaces it). */
export function isTaskFromMessageNote(m: { kind: string; metadata?: unknown }): boolean {
  return m.kind === 'system' && (m.metadata as { event?: unknown } | null | undefined)?.event === TASK_FROM_MESSAGE_EVENT;
}

/** "Carl Jones turned this into a task", "You turned this into a task", "Carl Jones linked a task". */
export function taskRowLine(link: Pick<MessageTaskLink, 'linked_by' | 'linked_by_name' | 'from_message'>, me: string | null | undefined, nameOf?: (id: string) => string | null | undefined): string {
  const who = link.linked_by && link.linked_by === me ? 'You'
    : (link.linked_by ? nameOf?.(link.linked_by) ?? link.linked_by_name : link.linked_by_name) || 'Someone';
  return `${who} ${link.from_message === false ? 'linked a task' : 'turned this into a task'}`;
}

/** Accessible summary of the row, e.g. "Carl Jones turned this into a task: TASK-2, Order a fire extinguisher, Done". */
export const taskRowLabel = (line: string, link: Pick<MessageTaskLink, 'key' | 'title' | 'status_name'>) => `${line}: ${link.key}, ${link.title}, ${link.status_name}`;

/** The status as plain coloured text: the status colour darkened (lightened in dark mode) so it reads on the tint,
 *  as the web's data-text utility. Not a colour we can read: slate. */
export function statusTextColor(hex: string | null | undefined, dark = false): string {
  if (!hex || !parseHex(hex)) return dark ? '#cbd5e1' : '#334155';
  return dark ? mixHex(hex, '#ffffff', 0.45) : mixHex(hex, '#000000', 0.55);
}
